// --- NEW FILE: packages/api-client/src/services/admin/adminTaskService.ts ---

import { getApiClient } from '../../client';
import { handleApiError } from '../../utils/handleApiError';
import type { 
    ServiceResponse,
    ProjectTask,
    TaskPayload,
    TaskChangeLog,
    RevertPayload,
    UUID,
} from '@dopomogai/types';

// The base URL prefix for top-level task operations that are not nested.
const ADMIN_TASKS_PREFIX = '/admin/tasks';
// The prefix for project-nested task operations.
const ADMIN_PROJECTS_PREFIX = '/admin/projects';

/**
 * A collection of functions for performing administrative operations
 * on ProjectTask entities.
 */
export const adminTaskService = {
  // =================================================================
  // ==                  TASK LIFECYCLE MANAGEMENT                  ==
  // =================================================================

  /**
   * Creates a new task within a specific project's global backlog.
   * @param projectId The ID of the project that will own this task.
   * @param data The multilingual payload for the new task.
   */
  createTask: (projectId: UUID, data: TaskPayload): ServiceResponse<ProjectTask> => {
    return getApiClient().post<ProjectTask>(`${ADMIN_TASKS_PREFIX}/projects/${projectId}/tasks`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `creating task '${data._id}' in project ${projectId}`));
  },

  /**
   * Fetches a list of tasks, scoped to a specific project.
   * Can optionally filter for tasks in a specific sprint or only unassigned tasks.
   * @param projectId The ID of the project to query.
   * @param params Optional filters for the task list.
   */
  listTasks: (projectId: UUID, params?: { sprint_id?: UUID; unassigned?: boolean; }): ServiceResponse<ProjectTask[]> => {
    return getApiClient().get<ProjectTask[]>(`${ADMIN_TASKS_PREFIX}/projects/${projectId}/tasks`, { params })
      .then(res => res.data)
      .catch(err => handleApiError(err, `listing tasks for project ${projectId}`));
  },

  /**
   * Updates an existing task's details and translations.
   * The backend intelligently handles patching the correct language pack.
   * @param taskId The ID of the task to update.
   * @param data The multilingual payload for the task update.
   */
  updateTask: (taskId: string, data: TaskPayload): ServiceResponse<ProjectTask> => {
    return getApiClient().put<ProjectTask>(`${ADMIN_TASKS_PREFIX}/${taskId}`, data)
      .then(res => res.data)
      .catch(err => handleApiError(err, `updating task ${taskId}`));
  },

  // Note: A 'deleteTask' function would go here, but since it's a highly destructive
  // action, it's often handled via an 'archive' status instead. If a hard delete is
  // needed, the endpoint would be: `DELETE /admin/tasks/{task_id}`.
  
  // =================================================================
  // ==                  TASK VERSIONING & REVERT                   ==
  // =================================================================

  /** Retrieves the granular version history (commit log) for a single task. */
  getTaskHistory: (taskId: string): ServiceResponse<TaskChangeLog[]> => {
    return getApiClient().get<TaskChangeLog[]>(`${ADMIN_TASKS_PREFIX}/${taskId}/history`)
      .then(res => res.data)
      .catch(err => handleApiError(err, `fetching history for task ${taskId}`));
  },

  /** Reverts a task to a previous version's state. */
  revertTask: (taskId: string, payload: RevertPayload): ServiceResponse<ProjectTask> => {
    return getApiClient().post<ProjectTask>(`${ADMIN_TASKS_PREFIX}/${taskId}/revert`, payload)
      .then(res => res.data)
      .catch(err => handleApiError(err, `reverting task ${taskId} to version ${payload.version}`));
  },
};